import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    ButtonBase,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Popover,
    Select,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Refresh,
    Add as AddIcon,
    Link as LinkIcon,
    LinkOff,
    Map as MapIcon,
    Tune as TuneIcon,
    FolderOpen as FolderOpenIcon,
    Cable as CableIcon,
    Air as ActuatorIcon,
    BatteryFull as BatteryNavIcon,
    SatelliteAlt as GnssIcon,
    SettingsInputAntenna as RadioIcon,
    SettingsOutlined as SettingsNavIcon,
    Article as MonitorIcon,
    Insights as TelemetryIcon,
    Security as SafetyIcon,
    VpnKey as VpnKeyIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    ConnectingAirports as ConnectingAirportsIcon,
    GitHub as GitHubIcon,
} from '@mui/icons-material';

import VimdronesLogo from './icons/vimdrones-logo.svg';
import ArdupilotLogo from './icons/ardupilot-logo.png';
import { filterModeList, loadModeFilter, CHANGE_EVENT as MODE_FILTER_CHANGE_EVENT } from './mode_catalog';
import { loadWsPresets, CHANGE_EVENT as WS_PRESETS_CHANGE_EVENT } from './connection_presets';

const SerialBaudRates = [9600, 19200, 38400, 57600, 115200, 230400, 460800, 921600];

const NAV_ITEMS = [
    { index: 0, label: 'Main', icon: <MapIcon fontSize="small" /> },
    { index: 3, label: 'Sensors', icon: <CableIcon fontSize="small" /> },
    { index: 6, label: 'GNSS', icon: <GnssIcon fontSize="small" /> },
    { index: 7, label: 'Radio', icon: <RadioIcon fontSize="small" /> },
    { index: 5, label: 'Battery', icon: <BatteryNavIcon fontSize="small" /> },
    { index: 4, label: 'Actuators', icon: <ActuatorIcon fontSize="small" /> },
    { index: 10, label: 'Safety', icon: <SafetyIcon fontSize="small" /> },
    { index: 11, label: 'Telemetry', icon: <TelemetryIcon fontSize="small" /> },
    { index: 2, label: 'FTP / Logs', icon: <FolderOpenIcon fontSize="small" /> },
    { index: 8, label: 'Settings', icon: <SettingsNavIcon fontSize="small" /> },
    { index: 1, label: 'Parameter', icon: <TuneIcon fontSize="small" /> },
    { index: 9, label: 'Mavlink Monitor', icon: <MonitorIcon fontSize="small" /> },
];

export const SIDEBAR_WIDTH = 200;

function friendlyConnectionError(raw) {
    const msg = (raw || '').toString();
    if (!msg) return 'Connection failed.';
    const lower = msg.toLowerCase();
    if (lower.includes("failed to open serial port") || lower.includes('failed to execute \'open\'')) {
        return 'Failed to open serial port. It may be in use by another app, or the device was unplugged.';
    }
    if (lower.includes('no serial port selected')) {
        return 'No serial port selected. Click + to authorize one first.';
    }
    if (lower.includes('websocket') && lower.includes('closed before open')) {
        return 'WebSocket refused the connection. Check the URL and that the server is running.';
    }
    if (lower.includes('websocket error')) {
        // Detect self-signed / untrusted TLS certificate hint embedded in the error.
        const certMatch = msg.match(/open (https?:\/\/[^\s)]+) in this browser/);
        if (certMatch) {
            return {
                message: 'WebSocket TLS certificate not trusted. Open the link to accept it in this browser, then try connecting again.',
                certUrl: certMatch[1],
            };
        }
        return { message: 'WebSocket error. Check the URL and network reachability.' };
    }
    if (lower.includes('websocket connection requires a url')) {
        return 'WebSocket URL is required.';
    }
    if (lower.includes('unsupported port type')) {
        return msg;
    }
    if (lower.includes('port opened then closed immediately')) {
        return 'Port opened then closed immediately. It may be in use by another app.';
    }
    if (lower.includes('connection dropped before any data')) {
        return 'Connection dropped before any data was received. Check baud rate and wiring.';
    }
    if (lower.includes('no mavlink data received')) {
        if (lower.includes('connection failed')) {
            return 'Connection failed: no MAVLink data received. Check the selected baud rate.';
        }
        return 'Disconnected: no MAVLink data received for 5 seconds.';
    }
    return `Connection failed: ${msg}`;
}

export default function SideBar({
    handleSelectApp,
    selectedAppIndex,
    selectedMavId,
    mavIdList,
    handleMavIdSelect,
    modeList,
    mavTimestamp,
}) {
    const [isConnected, setIsConnected] = useState(false);
    const [aboutOpen, setAboutOpen] = useState(false);
    const [modeFilter, setModeFilter] = useState(() => loadModeFilter());
    useEffect(() => {
        const onChange = (e) => setModeFilter((e && e.detail && e.detail.filter) || loadModeFilter());
        window.addEventListener(MODE_FILTER_CHANGE_EVENT, onChange);
        return () => window.removeEventListener(MODE_FILTER_CHANGE_EVENT, onChange);
    }, []);
    const visibleModeList = React.useMemo(
        () => filterModeList(modeList, mavIdList && mavIdList[selectedMavId], modeFilter),
        [modeList, mavIdList, selectedMavId, modeFilter],
    );
    const [connectButtonDisabled, setConnectButtonDisabled] = useState(false);
    // Tracks the in-flight intent: 'connect' | 'disconnect' | null. Only cleared
    // when the connection-health event reflects the requested state, so the
    // button cannot flicker back to enabled while the transition is pending.
    const pendingActionRef = useRef(null);
    const [pendingAction, setPendingActionState] = useState(null);
    const setPendingAction = (value) => {
        pendingActionRef.current = value;
        setPendingActionState(value);
    };
    const [modeId, setModeId] = useState(-1);
    const [displayMavId, setDisplayMavId] = useState(-1);

    const [availableSerialPorts, setAvailableSerialPorts] = useState([]);
    const [selectedSerialPort, setSelectedSerialPort] = useState('');
    const [selectedSerialBaudRate, setSelectedSerialBaudRate] = useState(115200);
    const [connectionType, setConnectionType] = useState(
        () => localStorage.getItem('connectionType') || 'serial',
    );
    const [wsUrl, setWsUrl] = useState(
        () => localStorage.getItem('connectionWsUrl') || 'ws://127.0.0.1:5555',
    );
    const [wsPresets, setWsPresets] = useState(() => loadWsPresets());
    useEffect(() => {
        const onChange = (e) => setWsPresets((e && e.detail && e.detail.presets) || loadWsPresets());
        window.addEventListener(WS_PRESETS_CHANGE_EVENT, onChange);
        return () => window.removeEventListener(WS_PRESETS_CHANGE_EVENT, onChange);
    }, []);
    const [signingKey, setSigningKey] = useState(
        () => localStorage.getItem('connectionSigningKey') || '',
    );
    // When the saved URL doesn't match any preset (e.g. presets changed in
    // Settings), default to the first available preset and sync its signing key.
    useEffect(() => {
        if (wsPresets.length === 0) return;
        const match = wsPresets.find((p) => p.url === wsUrl);
        if (match) return;
        const first = wsPresets[0];
        setWsUrl(first.url || '');
        localStorage.setItem('connectionWsUrl', first.url || '');
        const key = first.signingKey || '';
        setSigningKey(key);
        if (key) localStorage.setItem('connectionSigningKey', key);
        else localStorage.removeItem('connectionSigningKey');
    }, [wsPresets]); // eslint-disable-line react-hooks/exhaustive-deps
    const [paramProgress, setParamProgress] = useState({}); // { [mavId]: { received, total } }
    const [ftpProgress, setFtpProgress] = useState({}); // { [mavId]: { active, percent } }
    const [toast, setToast] = useState(null); // { severity, message } | null
    const connectTimerRef = useRef(null);

    const showToast = (severity, message) => setToast({ severity, message });

    const clearConnectTimer = () => {
        if (connectTimerRef.current) {
            clearTimeout(connectTimerRef.current);
            connectTimerRef.current = null;
        }
    };

    const onMavIdSelect = (event) => {
        handleMavIdSelect(event.target.value);
        setDisplayMavId(event.target.value);
    };

    const onModeSelect = (event) => {
        window.api.setMode(selectedMavId, event.target.value);
    };

    const validateWsUrl = (value) => {
        if (!value) return { valid: false, reason: 'URL is required' };
        let u;
        try {
            u = new URL(value);
        } catch (_) {
            return { valid: false, reason: 'Invalid URL' };
        }
        if (u.protocol !== 'ws:' && u.protocol !== 'wss:') {
            return { valid: false, reason: 'Must start with ws:// or wss://' };
        }
        if (!u.hostname) return { valid: false, reason: 'Missing host' };
        if (u.port !== '' && !/^\d+$/.test(u.port)) {
            return { valid: false, reason: 'Invalid port' };
        }
        return { valid: true };
    };
    const wsUrlValidation = validateWsUrl(wsUrl);
    const isWsUrlValid = wsUrlValidation.valid;

    const connectButtonShouldBeDisabled = () => {
        if (connectionType === 'websocket') {
            if (!isWsUrlValid) return true;
            return connectButtonDisabled;
        }
        if (selectedSerialPort === '') return false;
        return connectButtonDisabled;
    };

    const handleConnect = () => {
        setPendingAction('connect');
        setConnectButtonDisabled(true);
        setIsConnected(false);
        clearConnectTimer();
        // Heartbeat timeout: if no health within 10 s after the transport
        // opened successfully, surface a hint and re-enable the button.
        connectTimerRef.current = setTimeout(() => {
            if (pendingActionRef.current === 'connect') {
                setPendingAction(null);
                setConnectButtonDisabled(false);
                setIsConnected(false);
                showToast('warning', 'No heartbeat received. Check baud rate / URL / wiring.');
                try { window.api.disconnectFlightController(); } catch (e) { /* ignore */ }
            }
        }, 10000);
        if (connectionType === 'websocket') {
            localStorage.setItem('connectionWsUrl', wsUrl);
            window.api.connectFlightController({ type: 'websocket', url: wsUrl, signingKey });
            return;
        }
        const portEntry = availableSerialPorts.find((p) => p.path === selectedSerialPort);
        window.api.connectFlightController({
            type: 'serial',
            port: portEntry ? portEntry._port : selectedSerialPort,
            baudRate: selectedSerialBaudRate,
            // Keep serial independent from WebSocket presets to avoid persisted
            // ws signing keys breaking later serial connections.
            signingKey: '',
        });
    };

    const handleConnectionTypeChange = (event) => {
        const value = event.target.value;
        setConnectionType(value);
        localStorage.setItem('connectionType', value);
    };

    const handleDisconnect = () => {
        setPendingAction('disconnect');
        setConnectButtonDisabled(true);
        clearConnectTimer();
        window.api.disconnectFlightController();
    };

    const handleRefreshSerialPorts = () => window.api.getAvailableSerialPorts();
    const handleRequestSerialPort = () => window.api.requestSerialPort();

    const handleAvailableSerialPorts = (event, ports) => {
        setAvailableSerialPorts(ports);
        if (!selectedSerialPort && ports.length > 0) {
            setSelectedSerialPort(ports[0].path);
        }
    };

    const handleFlightControllerConnectionHealth = (event, health) => {
        const pending = pendingActionRef.current;
        // Only clear the disabled state when the health event matches the
        // requested transition. Otherwise the button briefly re-enables before
        // the next health tick arrives.
        if (pending === 'connect' && health) {
            setPendingAction(null);
            setConnectButtonDisabled(false);
            clearConnectTimer();
        } else if (pending === 'disconnect' && !health) {
            setPendingAction(null);
            setConnectButtonDisabled(false);
            clearConnectTimer();
        } else if (!pending && connectButtonDisabled) {
            setConnectButtonDisabled(false);
        }
        setIsConnected((prev) => {
            if (health) {
                if (!prev) {
                    // Reset any stale progress on new connection
                    setParamProgress({});
                    return true;
                }
            } else {
                if (prev) return false;
            }
            return prev;
        });
    };

    const handleParamProgress = (event) => {
        const { mavId, received, total } = event.detail || {};
        if (!mavId) return;
        setParamProgress((prev) => ({ ...prev, [mavId]: { received, total } }));
    };

    const handleFtpProgress = (event) => {
        const { mavId, active, percent } = event.detail || {};
        if (mavId == null) return;
        setFtpProgress((prev) => ({ ...prev, [mavId]: { active: !!active, percent } }));
    };

    useEffect(() => {
        window.addEventListener('param-progress', handleParamProgress);
        window.addEventListener('ftp-progress', handleFtpProgress);
        return () => {
            window.removeEventListener('param-progress', handleParamProgress);
            window.removeEventListener('ftp-progress', handleFtpProgress);
        };
    }, []);

    useEffect(() => {
        window.api.onFlightControllerConnectionHealth(handleFlightControllerConnectionHealth);
        return () => {
            window.api.removeOnFlightControllerConnectionHealth(handleFlightControllerConnectionHealth);
        };
    }, [connectButtonDisabled]);

    useEffect(() => {
        const handleConnectionStatus = (event, status) => {
            if (!status) return;
            if (status.success === false) {
                if (pendingActionRef.current === 'connect') {
                    setPendingAction(null);
                    setConnectButtonDisabled(false);
                    clearConnectTimer();
                }
                setIsConnected(false);
                // Long-running MAVLink silence is presented by the global
                // OFFLINE modal; avoid showing a duplicate sidebar toast.
                if (status.disconnected) return;
                const connErr = friendlyConnectionError(status.message);
                if (connErr && typeof connErr === 'object') {
                    setToast({ severity: 'error', message: connErr.message, certUrl: connErr.certUrl });
                } else {
                    showToast('error', connErr);
                }
            }
        };
        window.api.onConnectionStatus(handleConnectionStatus);
        return () => {
            window.api.removeOnConnectionStatus(handleConnectionStatus);
        };
    }, []);

    useEffect(() => () => clearConnectTimer(), []);

    useEffect(() => {
        if (availableSerialPorts.length === 0) {
            window.api.getAvailableSerialPorts();
        }
        window.api.onAvailableSerialPorts(handleAvailableSerialPorts);
        return () => {
            window.api.removeOnAvailableSerialPorts(handleAvailableSerialPorts);
        };
    }, []);

    useEffect(() => {
        if (!window.mav) return;
        const vehicle = window.mav[selectedMavId];
        if (!vehicle) return;
        if (vehicle.HEARTBEAT) {
            const currentModeId = vehicle.HEARTBEAT.custom_mode;
            if (currentModeId in (modeList || {}) && modeId !== currentModeId) {
                setModeId(currentModeId);
            }
            if (selectedMavId in (mavIdList || {})) {
                setDisplayMavId(selectedMavId);
            }
        }
    }, [mavTimestamp, selectedMavId]);

    const sectionTitleSx = {
        px: 2,
        pt: 1.5,
        pb: 0.5,
        fontSize: 11,
        letterSpacing: 1,
        textTransform: 'uppercase',
        color: 'text.secondary',
    };

    return (
        <Box
            component="aside"
            sx={{
                width: SIDEBAR_WIDTH,
                flexShrink: 0,
                height: '100vh',
                bgcolor: 'background.paper',
                borderRight: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                overflowX: 'hidden',
            }}
        >
            {/* Brand */}
            <Box sx={{ px: 2, py: 1.25, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1, letterSpacing: 0.3, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <ConnectingAirportsIcon sx={{ color: 'primary.main', fontSize: 22, position: 'absolute', transform: 'translateX(-28px)' }} />
                    Lite GCS
                </Typography>
            </Box>
            <Divider />

            {/* Navigation */}
            <List dense disablePadding>
                {NAV_ITEMS.map((item) => {
                    const isParam = item.index === 1;
                    const isFtp = item.index === 2;
                    const p = isParam ? paramProgress[selectedMavId] : null;
                    const paramPct = p && p.total ? Math.min(100, (p.received / p.total) * 100) : 0;
                    const paramInProgress = isParam && p && p.total && p.received < p.total;
                    const ftp = isFtp ? ftpProgress[selectedMavId] : null;
                    const ftpInProgress = isFtp && ftp && ftp.active;
                    const ftpPct = ftp && typeof ftp.percent === 'number' ? Math.min(100, ftp.percent) : null;
                    const inProgress = paramInProgress || ftpInProgress;
                    const determinate = paramInProgress || (ftpInProgress && ftpPct != null);
                    const pct = paramInProgress ? paramPct : ftpPct;
                    return (
                        <ListItemButton
                            key={item.index}
                            selected={selectedAppIndex === item.index}
                            onClick={() => handleSelectApp(item.index)}
                            sx={{ px: 2, py: 0.75 }}
                        >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                {inProgress ? (
                                    <Box sx={{ position: 'relative', display: 'inline-flex', width: 20, height: 20, alignItems: 'center', justifyContent: 'center' }}>
                                        <CircularProgress
                                            variant={determinate ? 'determinate' : 'indeterminate'}
                                            value={determinate ? pct : undefined}
                                            size={20}
                                            thickness={4}
                                            sx={{ color: 'warning.main' }}
                                        />
                                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 12 } }}>
                                            {item.icon}
                                        </Box>
                                    </Box>
                                ) : item.icon}
                            </ListItemIcon>
                            <ListItemText primary={item.label} />
                        </ListItemButton>
                    );
                })}
            </List>

            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            {/* VimDrones logo above Connection */}
            <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="About Lite GCS" placement="top">
                    <ButtonBase
                        onClick={() => setAboutOpen(true)}
                        sx={{ borderRadius: 1, p: 0.5, '&:hover': { opacity: 1, bgcolor: 'action.hover' } }}
                    >
                        <Box
                            component="img"
                            src={VimdronesLogo}
                            alt="VimDrones"
                            sx={{ height: 28, width: 'auto', objectFit: 'contain', opacity: 0.9, display: 'block' }}
                        />
                    </ButtonBase>
                </Tooltip>
            </Box>

            <Dialog open={aboutOpen} onClose={() => setAboutOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <ConnectingAirportsIcon sx={{ color: 'primary.main' }} />
                    About Lite GCS
                    <Chip
                        label={`v${process.env.APP_VERSION}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontFamily: 'monospace', fontSize: 10, '& .MuiChip-label': { px: 0.75 } }}
                    />
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} alignItems="center" sx={{ pt: 1 }}>
                        <Typography variant="body1" align="center">
                            A Lightweight GCS running in your browser
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                                Supported Autopilot
                            </Typography>
                            <Link
                                href="https://ardupilot.org/"
                                target="_blank"
                                rel="noopener"
                                sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
                            >
                                <Box
                                    component="img"
                                    src={ArdupilotLogo}
                                    alt="ArduPilot"
                                    sx={{ height: 16, width: 'auto', objectFit: 'contain' }}
                                />
                            </Link>
                        </Stack>
                        <Divider flexItem />
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ width: '100%', justifyContent: 'space-between' }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="caption" color="text.secondary">Author</Typography>
                                <Link
                                    href="https://github.com/Huibean"
                                    target="_blank"
                                    rel="noopener"
                                    variant="caption"
                                    underline="hover"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                >
                                    <GitHubIcon sx={{ fontSize: 14 }} />
                                    Huibean Luo
                                </Link>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                    Sponsored by
                                </Typography>
                                <Link
                                    href="https://dev.vimdrones.com"
                                    target="_blank"
                                    rel="noopener"
                                    sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
                                >
                                    <Box
                                        component="img"
                                        src={VimdronesLogo}
                                        alt="VimDrones"
                                        sx={{ height: 28, width: 'auto', objectFit: 'contain' }}
                                    />
                                </Link>
                            </Stack>
                        </Stack>
                    </Stack>
                </DialogContent>
            </Dialog>

            <Divider />

            {/* Connection / Vehicle */}
            <Typography sx={sectionTitleSx}>Connection</Typography>
            <Stack spacing={1} sx={{ px: 2, pb: 2 }}>
                {!isConnected && connectionType === 'serial' ? (
                    <>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="connection-editor-serial-port-label">Port</InputLabel>
                            <Select
                                labelId="connection-editor-serial-port-label"
                                label="Port"
                                value={selectedSerialPort}
                                onChange={(event) => setSelectedSerialPort(event.target.value)}
                            >
                                {availableSerialPorts.map((port) => (
                                    <MenuItem key={port.path} value={port.path}>{port.path}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <FormControl size="small" sx={{ flexGrow: 1 }}>
                                <InputLabel id="connection-editor-baud-rate-label">Baud</InputLabel>
                                <Select
                                    labelId="connection-editor-baud-rate-label"
                                    label="Baud"
                                    value={selectedSerialBaudRate}
                                    onChange={(event) => setSelectedSerialBaudRate(event.target.value)}
                                >
                                    {SerialBaudRates.map((baudRate) => (
                                        <MenuItem key={baudRate} value={baudRate}>{baudRate}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Tooltip title="Refresh ports">
                                <span>
                                    <IconButton onClick={handleRefreshSerialPorts}>
                                        <Refresh fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Authorize new port">
                                <span>
                                    <IconButton onClick={handleRequestSerialPort}>
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    </>
                ) : !isConnected ? (
                    <FormControl size="small" fullWidth error={wsUrl.length > 0 && !isWsUrlValid}>
                        <InputLabel id="ws-url-label">URL</InputLabel>
                        <Select
                            labelId="ws-url-label"
                            label="URL"
                            value={wsPresets.some((p) => p.url === wsUrl) ? wsUrl : ''}
                            onChange={(e) => {
                                const url = e.target.value;
                                setWsUrl(url);
                                const preset = wsPresets.find((p) => p.url === url);
                                if (preset && typeof preset.signingKey === 'string') {
                                    setSigningKey(preset.signingKey);
                                    if (preset.signingKey) localStorage.setItem('connectionSigningKey', preset.signingKey);
                                    else localStorage.removeItem('connectionSigningKey');
                                }
                            }}
                            renderValue={(value) => {
                                const preset = wsPresets.find((p) => p.url === value);
                                const label = (preset && preset.label) || value || '';
                                return (
                                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>{label}</Typography>
                                        <Tooltip title={preset && preset.signingKey ? 'Signing key set' : 'No signing key'}>
                                            <VpnKeyIcon
                                                fontSize="small"
                                                sx={{ color: preset && preset.signingKey ? 'success.main' : 'text.disabled' }}
                                            />
                                        </Tooltip>
                                    </Stack>
                                );
                            }}
                        >
                            {wsPresets.length === 0 && (
                                <MenuItem disabled value="">
                                    <em>No presets — add one in Settings → Connection</em>
                                </MenuItem>
                            )}
                            {wsPresets.map((p) => (
                                <MenuItem key={p.id} value={p.url}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, width: '100%' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                            <Typography variant="body2" noWrap>{p.label || p.url}</Typography>
                                            {p.label && p.label !== p.url && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }} noWrap>
                                                    {p.url}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Tooltip title={p.signingKey ? 'Signing key set' : 'No signing key'}>
                                            <VpnKeyIcon
                                                fontSize="small"
                                                sx={{ color: p.signingKey ? 'success.main' : 'text.disabled' }}
                                            />
                                        </Tooltip>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : null}
                {isConnected && (
                    <>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="mav-id-label">SYS ID</InputLabel>
                            <Select
                                labelId="mav-id-label"
                                id="mav-id"
                                value={
                                    (displayMavId !== -1
                                        && Object.prototype.hasOwnProperty.call(mavIdList || {}, String(displayMavId)))
                                        ? String(displayMavId)
                                        : ''
                                }
                                label="SYS ID"
                                onChange={onMavIdSelect}
                            >
                                {Object.keys(mavIdList || {}).map((key) => (
                                    <MenuItem key={key} value={key}>
                                        {`${mavIdList[key]}:${key}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="flight-mode-label">Mode</InputLabel>
                            <Select
                                labelId="flight-mode-label"
                                id="flight-mode"
                                value={(modeId !== -1 && visibleModeList && Object.prototype.hasOwnProperty.call(visibleModeList, String(modeId))) ? String(modeId) : ''}
                                label="Mode"
                                onChange={onModeSelect}
                            >
                                {Object.keys(visibleModeList || {}).map((key) => (
                                    <MenuItem key={key} value={key}>{visibleModeList[key]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </>
                )}
                {!isConnected && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <FormControl size="small" sx={{ flexGrow: 1 }}>
                            <InputLabel id="connection-type-label">Type</InputLabel>
                            <Select
                                labelId="connection-type-label"
                                label="Type"
                                value={connectionType}
                                onChange={handleConnectionTypeChange}
                            >
                                <MenuItem value="serial">Serial</MenuItem>
                                <MenuItem value="websocket">WebSocket</MenuItem>
                            </Select>
                        </FormControl>
                        {pendingAction ? (
                            <Tooltip title={pendingAction === 'connect' ? 'Connecting…' : 'Disconnecting…'}>
                                <span>
                                    <IconButton color="primary" disabled>
                                        <CircularProgress size={18} thickness={5} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ) : (
                            <Tooltip title="Connect">
                                <span>
                                    <IconButton
                                        color="primary"
                                        onClick={handleConnect}
                                        disabled={connectButtonShouldBeDisabled()}
                                    >
                                        <LinkIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>
                )}
                {isConnected && (() => {
                    const fullSummary = connectionType === 'serial'
                        ? `${selectedSerialPort || '—'} · ${selectedSerialBaudRate}`
                        : `${wsUrl}`;
                    const shortSummary = connectionType === 'serial'
                        ? `${selectedSerialPort || '—'} @ ${selectedSerialBaudRate}`
                        : (wsUrl || '').replace(/^wss?:\/\//, '');
                    return (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title={fullSummary}>
                                <Typography
                                    variant="caption"
                                    noWrap
                                    sx={{
                                        flexGrow: 1,
                                        minWidth: 0,
                                        color: 'text.secondary',
                                        fontFamily: 'monospace',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {shortSummary}
                                </Typography>
                            </Tooltip>
                            {pendingAction ? (
                                <Tooltip title={pendingAction === 'connect' ? 'Connecting…' : 'Disconnecting…'}>
                                    <span>
                                        <IconButton size="small" color="primary" disabled>
                                            <CircularProgress size={16} thickness={5} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Tooltip title="Disconnect">
                                    <span>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={handleDisconnect}
                                            disabled={connectButtonShouldBeDisabled()}
                                        >
                                            <LinkOff fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </Stack>
                    );
                })()}
            </Stack>
            <Snackbar
                open={!!toast}
                autoHideDuration={toast && toast.certUrl ? null : (toast && toast.severity === 'error' ? 6000 : 3000)}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {toast ? (
                    <Alert
                        onClose={() => setToast(null)}
                        severity={toast.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {toast.message}
                        {toast.certUrl && (
                            <Link
                                href={toast.certUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'block', mt: 0.75, color: 'inherit', fontWeight: 600 }}
                            >
                                Open certificate page →
                            </Link>
                        )}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Box>
    );
}
